import { useEffect, useRef, useState } from 'react';
import { Text } from '~/components/ui/Text';
import {
	StyledContainer,
	StyledIconContainer,
	StyledInfoContainer,
	StyledProgress,
	StyledProgressContainer,
	StyledProgressWrapper,
	StyledTopContainer,
} from './VehicleStateItem.styled';

const ANIMATION_DURATION_MS = 1200;

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const VehicleStateItem = ({
	name,
	percent,
	icon,
}: {
	name: string;
	percent: number;
	icon: string;
}) => {
	const [animatedPercent, setAnimatedPercent] = useState(percent);
	const rafRef = useRef<number | null>(null);
	const fromRef = useRef(percent);
	const isFirstRunRef = useRef(true);

	useEffect(() => {
		if (isFirstRunRef.current) {
			isFirstRunRef.current = false;
			fromRef.current = percent;
			setAnimatedPercent(percent);
			return;
		}

		const from = fromRef.current;
		const to = percent;

		if (from === to) {
			return;
		}

		const startTs = performance.now();

		const tick = (now: number) => {
			const elapsed = now - startTs;
			const progress = Math.min(elapsed / ANIMATION_DURATION_MS, 1);
			const eased = easeOutCubic(progress);
			const value = from + (to - from) * eased;

			setAnimatedPercent(value);
			fromRef.current = value;

			if (progress < 1) {
				rafRef.current = requestAnimationFrame(tick);
			} else {
				fromRef.current = to;
				rafRef.current = null;
			}
		};

		if (rafRef.current !== null) {
			cancelAnimationFrame(rafRef.current);
		}

		rafRef.current = requestAnimationFrame(tick);

		return () => {
			if (rafRef.current !== null) {
				cancelAnimationFrame(rafRef.current);
				rafRef.current = null;
			}
		};
	}, [percent]);

	const displayValue = Math.round(animatedPercent);
	const isCritical = displayValue <= 30;

	return (
		<StyledContainer>
			<StyledIconContainer>
				<img src={icon} />
			</StyledIconContainer>
			<StyledInfoContainer>
				<StyledTopContainer>
					<Text>{name}</Text>
					<Text
						style={{ color: isCritical ? '#C71E1E' : '#FFFFFF' }}
					>{`${displayValue}%`}</Text>
				</StyledTopContainer>
				<StyledProgressContainer>
					<StyledProgressWrapper>
						<StyledProgress
							style={{
								width: `${Math.max(0, Math.min(100, animatedPercent))}%`,
								backgroundColor: isCritical ? '#C71E1E' : '#2D70DA',
							}}
						/>
					</StyledProgressWrapper>
				</StyledProgressContainer>
			</StyledInfoContainer>
		</StyledContainer>
	);
};

export { VehicleStateItem };
